// What a chat shows about its process (legacy decision 3, Kai, 11 Oct): a
// badge while the process is live (Working: a turn in flight; Ready: live, no
// turn), and one Stop item under the three-dots menu. Nothing is paused,
// resumed, restored, snapshotted or published: a chat is there, you type, it
// answers, and a stopped chat starts again on its next message.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Box, Chip, Snackbar } from '@mui/material'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import axios from 'axios'
import { API_BASE_URL } from '../../constants/system'
import MenuActionsButton, { type MenuAction } from '../system/MenuActionsButton'

/** How often the chat's process state is read. */
const STATUS_POLL_MS = 2500

/** Stop is refused for a process that is not running; this is the sentence under it. */
export const NOT_RUNNING_REASON = "This chat isn't running. Your next message starts it."

/**
 * The route that stops a chat's process and leaves its folder
 * (goapi/pkg/server/agent_stop.go). The chat's owner or a Manager only: the
 * server answers anyone else 403 with the reason. This is the one place the
 * frontend names it.
 */
const stopUrl = (base: string) => `${base}/stop`

interface StatusResponse {
  runtime_state?: string
  sandboxState?: string
  activeQuery?: { queryId: string } | null
}

interface Props {
  customer: string
  sessionId: string
  /** True while this page is streaming an answer: the badge says Working at once. */
  chatStreaming?: boolean
  /**
   * Why this person may not stop the chat (only its owner or a Manager may),
   * worded by utils/permissionReason. Stop is then shown disabled with it.
   */
  stopRefusal?: string
  /** Fires when the process state changes ('running' or what the server says instead), for the History badge. */
  onLiveStateChange?: (state: string) => void
  /** The host's own entries, listed below Stop. */
  extraActions?: MenuAction[]
}

export default function SessionProcessControl({ customer, sessionId, chatStreaming, stopRefusal, onLiveStateChange, extraActions }: Props) {
  const base = `${API_BASE_URL}/agentkit/agent/${customer}/session/${sessionId}`
  const [state, setState] = useState('')
  const [turnActive, setTurnActive] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Read by the poll, which must not restart when the host passes a new callback.
  const onLiveRef = useRef(onLiveStateChange)
  onLiveRef.current = onLiveStateChange
  const reportedRef = useRef('')

  const readStatus = useCallback(async () => {
    const resp = await axios.get<StatusResponse>(`${base}/status`)
    const next = resp.data.runtime_state ?? resp.data.sandboxState ?? ''
    setState(next)
    setTurnActive(!!resp.data.activeQuery?.queryId)
    if (next && next !== reportedRef.current) {
      reportedRef.current = next
      onLiveRef.current?.(next)
    }
  }, [base])

  // Trigger: the chat this control is for. A failed read keeps the last answer.
  useEffect(() => {
    setState('')
    setTurnActive(false)
    reportedRef.current = ''
    readStatus().catch(() => {})
    const timer = setInterval(() => { readStatus().catch(() => {}) }, STATUS_POLL_MS)
    return () => clearInterval(timer)
  }, [readStatus])

  const running = state === 'running'

  const stop = useCallback(async () => {
    setError(null)
    setStopping(true)
    try {
      await axios.post(stopUrl(base), {})
    } catch (e) {
      const msg = axios.isAxiosError(e) ? (e.response?.data?.error || e.message) : e instanceof Error ? e.message : String(e)
      setError(`Stop failed: ${msg}`)
    } finally {
      setStopping(false)
      await readStatus().catch(() => {})
    }
  }, [base, readStatus])

  const stopReason = stopRefusal ?? (running ? undefined : NOT_RUNNING_REASON)
  const actions: MenuAction[] = [
    {
      title: 'Stop',
      icon: <StopCircleOutlinedIcon fontSize="small" />,
      onClick: stop,
      disabled: !!stopReason || stopping,
      reason: stopReason,
    },
    ...(extraActions ?? []),
  ]

  const working = running && (turnActive || !!chatStreaming)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {running && (
        <Chip
          data-testid="session-status-pill"
          size="small"
          label={working ? 'Working' : 'Ready'}
          // The History badge's two greens: filled for a turn in flight, quiet for live and idle.
          sx={working
            ? { backgroundColor: '#16a34a', color: '#fff', fontWeight: 600, '& .MuiChip-label': { color: '#fff' } }
            : { backgroundColor: '#ecfdf5', color: '#15803d', '& .MuiChip-label': { color: '#15803d' } }}
        />
      )}
      <MenuActionsButton actions={actions} tooltip="Manage chat" ariaLabel="Manage chat" />
      <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)} variant="filled">{error}</Alert>
      </Snackbar>
    </Box>
  )
}
